import { FC, useState, useCallback, useEffect, useRef } from "react";

import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import { Typography, Link } from "@portex-pro/ui-components";
import greenscreenIconTransparentBG from "assets/greenscreens-icon-transparent.png";
import greenscreenIcon from "assets/greenscreens-icon.png";
import portexIcon from "assets/portex-icon.png";
import Frame from "components/Frame";
import { Maybe } from "graphql/jsutils/Maybe";
import first from "lodash/first";
import last from "lodash/last";
import orderBy from "lodash/orderBy";
import { useTranslation } from "react-i18next";

import { PublicLocation, PublicQuoteRequest } from "../../../api/types/generated-types";
import { GREENSCREENS_PARTNERSHIP_LINK } from "../../../constants/links";
import { TrailerType, TrailerTypeEnum } from "../../../types/TrailerType";
import { useBenchmarkRateEstimatorStore } from "../hooks/useBenchmarkRateEstimatorStore";
import { useGreenscreensAuth } from "../hooks/useGreenscreensAuth";
import { Address, InputValues } from "../types/Greenscreens.types";
import GreenscreensWidget from "./GreenscreensWidget";
import PartnerComingSoonDialog from "./PartnerComingSoonDialog";
import PartnerConnectDialog from "./PartnerConnectDialog";

type BenchmarkRateEstimatesViewProps = {
  publicQuoteRequest: PublicQuoteRequest;
};

type PartnerConfig = {
  name: string;
  logo: string;
};

const BenchmarkRateEstimatesView: FC<BenchmarkRateEstimatesViewProps> = ({ publicQuoteRequest }) => {
  const { t } = useTranslation(["benchmarkRateEstimates"]);
  const [showDialog, setShowDialog] = useState(false);
  const [showComingSoonDialog, setShowComingSoonDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<PartnerConfig | null>(null);
  const { token, error, isLoading, authenticate } = useGreenscreensAuth();
  const hasInitialized = useRef(false);

  const { partners, setPartnerAuth, resetPartnerAuth } = useBenchmarkRateEstimatorStore();

  const greenscreensEmail = partners.greenscreens.email ?? "";
  const greenscreensConnected = partners.greenscreens.connected ?? false;

  useEffect(() => {
    if (hasInitialized.current) return;
    if (greenscreensEmail && greenscreensConnected) {
      authenticate();
    }
    hasInitialized.current = true;
  }, [authenticate, greenscreensEmail, greenscreensConnected]);

  const accessTokenProvider = useCallback(() => {
    if (!token) throw new Error("No token available");
    return `Bearer ${token}`;
  }, [token]);

  const mapPublicQuoteRequestToInputValues = (quote: PublicQuoteRequest): InputValues => {
    const locations = quote.locations?.filter(Boolean) ?? [];
    const orderedLocations = orderBy(locations, "position");
    const origin = first(orderedLocations);
    const destination = last(orderedLocations);
    const extraStops = locations.filter((l) => l !== origin && l !== destination);

    const toAddress = (loc: Maybe<PublicLocation>): Address => {
      if (!loc) return { zip: "" };
      const hasZip = !!loc.zip;
      const hasCity = !!loc.city;
      const hasState = !!loc.province_code;
      if (hasZip && hasCity && hasState) return { zip: loc.zip, city: loc.city, state: loc.province_code ?? "" };
      if (hasCity && hasState) return { city: loc.city, state: loc.province_code ?? "" };
      if (hasZip) return { zip: loc.zip };
      return { zip: "" };
    };

    const mapTrailerTypeToTruck = (type?: TrailerType): "VAN" | "REEFER" | "FLATBED" => {
      switch (type) {
        case TrailerTypeEnum.DRY_VAN:
        case TrailerTypeEnum.BOX_TRUCK:
        case TrailerTypeEnum.CARGO_VAN:
        case TrailerTypeEnum.SPRINTER_VAN:
          return "VAN";
        case TrailerTypeEnum.REEFER:
        case TrailerTypeEnum.DRAYAGE_REEFER:
          return "REEFER";
        case TrailerTypeEnum.FLATBED:
        case TrailerTypeEnum.STEP_DECK:
        case TrailerTypeEnum.CONESTOGA:
          return "FLATBED";
        default:
          return "VAN";
      }
    };

    return {
      truck: mapTrailerTypeToTruck((quote.trailer_type as TrailerType) || undefined),
      origin: toAddress(origin),
      destination: toAddress(destination),
      extraStops: extraStops.map(toAddress),
      pickupDate: quote.cargo_ready_date ? new Date(quote.cargo_ready_date) : undefined,
      deliveryDate: quote.target_delivery_date ? new Date(quote.target_delivery_date) : undefined,
      weight: quote.total_weight ?? 1000,
      commodity: quote.commodities ?? "General Freight",
      customer: quote.shipper_name ?? "Portex",
      currency: "USD",
    };
  };

  const showPartnerDialog = (partner: PartnerConfig, comingSoon = false) => {
    setSelectedPartner(partner);
    if (comingSoon) {
      setShowComingSoonDialog(true);
    } else {
      setShowDialog(true);
    }
  };

  const logout = () => {
    resetPartnerAuth("greenscreens");
    setSelectedPartner(null);
  };

  const showGreenScreensSection = token && greenscreensEmail.length > 0 && greenscreensConnected;

  return (
    <>
      <Frame
        collapsible
        defaultOpen
        title={t("benchmarkRateEstimates:benchRateEstimatesTitle", "Benchmark Rate Estimates")}
        className="w-full flex flex-col"
        actions={
          showGreenScreensSection && (
            <Button
              variant="text"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                logout();
              }}
            >
              {t("benchmarkRateEstimates:logoutButton", "Logout")}
            </Button>
          )
        }
      >
        {showGreenScreensSection && (
          <>
            <Divider className="my-6" />
            <div className="pb-6 space-y-4">
              <div className="flex items-center space-x-2 my-6">
                <img src={greenscreenIcon} alt="Greenscreens" className="h-10" />
                <Typography variant="body1">
                  <strong>
                    {t("benchmarkRateEstimates:greenscreensWidgetDataTitle", "Data from Greenscreens AI")}
                  </strong>
                </Typography>
              </div>

              <GreenscreensWidget
                accessTokenProvider={accessTokenProvider}
                user={{ email: greenscreensEmail }}
                inputValues={mapPublicQuoteRequestToInputValues(publicQuoteRequest)}
                widgets={{
                  targetBuyRate: {},
                  targetSellRate: {},
                  networkRates: {},
                }}
              />
            </div>
          </>
        )}
        {!showGreenScreensSection && (
          <div className="flex flex-col items-center p-6 space-y-4 text-center">
            <div className="w-20 h-20 rounded-md bg-blue-50 flex items-center justify-center">
              <img src={greenscreenIconTransparentBG} alt="GreenScreens AI icon" className="h-11 w-[57px]" />
            </div>

            <Typography variant="h6" align="center">
              <strong>{t("benchmarkRateEstimates:greenscreensLabel", "Greenscreens AI")}</strong>
            </Typography>
            <Typography variant="body1" align="center" style={{ color: "#6B7280" }}>
              {t(
                "benchmarkRateEstimates:greenscreensDescription",
                "Access real-time benchmark rates with Greenscreens, the AI-powered pricing and market intelligence platform."
              )}
            </Typography>
            <div className="flex items-center space-x-2">
              <div className="flex-1 min-w-[140px]">
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => showPartnerDialog({ name: "Greenscreens.ai", logo: greenscreenIcon })}
                >
                  {t("benchmarkRateEstimates:connectButton", "Connect")}
                </Button>
              </div>
              <div className="flex-1 min-w-[140px]">
                <Link
                  href={GREENSCREENS_PARTNERSHIP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Button fullWidth variant="outlined">
                    {t("benchmarkRateEstimates:learnMoreButton", "Learn More")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </Frame>

      <PartnerConnectDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onConfirm={async (email: string) => {
          await authenticate();
          setPartnerAuth("greenscreens", {
            connected: true,
            email,
          });
          setShowDialog(false);
        }}
        partnerName={selectedPartner?.name || ""}
        partnerLogo={selectedPartner?.logo || greenscreenIcon}
        portexLogo={portexIcon}
        permissions={[
          t("benchmarkRateEstimates:greenscreensPermission1", "View quote pricing"),
          t("benchmarkRateEstimates:greenscreensPermission2", "Share predictive benchmark estimates"),
        ]}
      />

      <PartnerComingSoonDialog
        open={showComingSoonDialog}
        onClose={() => setShowComingSoonDialog(false)}
        partnerName={selectedPartner?.name || ""}
        partnerLogo={selectedPartner?.logo || greenscreenIcon}
        portexLogo={portexIcon}
        permissions={[]}
      />

      {error && <div className="text-red-500 text-center mt-4">{error.message}</div>}

      {isLoading && (
        <div className="text-center mt-4 text-gray-600">
          {t("benchmarkRateEstimates:loadingAuth", "Authenticating with Greenscreens...")}
        </div>
      )}
    </>
  );
};

export default BenchmarkRateEstimatesView;
